import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../../api";
import { Building2, Plus, Power, PowerOff, Pencil, ExternalLink, LifeBuoy } from "lucide-react";

const ROOT_DOMAIN = process.env.REACT_APP_PLATFORM_ROOT_DOMAIN || "filagestao.com.br";

export default function SuperTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super/tenants");
      setTenants(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (t) => {
    const action = t.active ? "deactivate" : "activate";
    try {
      await api.post(`/super/tenants/${t.id}/${action}`);
      toast.success(t.active ? "Cliente desativado" : "Cliente ativado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openSupport = async (t) => {
    try {
      const { data } = await api.post(`/super/tenants/${t.id}/support-session`);
      const url = `${window.location.origin}/support/enter?token=${encodeURIComponent(data.token)}&slug=${encodeURIComponent(data.tenant_slug)}`;
      // Abre em nova aba para preservar a listagem de clientes do Super Admin.
      const w = window.open(url, "_blank", "noopener");
      if (!w) {
        toast.info("Habilite pop-ups para abrir o Modo Suporte em nova aba.");
      } else {
        toast.success(`Modo Suporte iniciado em ${t.display_name || t.name}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="super-tenants-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Clientes</h1>
          <p className="text-sm text-zinc-500">Estabelecimentos ativos na plataforma FILA Gestão</p>
        </div>
        <Link
          to="/super/clientes/novo"
          data-testid="super-btn-novo-cliente"
          className="inline-flex items-center gap-2 rounded-full bg-brand-500 px-5 py-2 text-sm font-semibold text-white shadow-sm shadow-brand-500/20 hover:bg-brand-600 hover:-translate-y-[1px] transition-all"
        >
          <Plus size={14} /> Novo Cliente
        </Link>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden shadow-sm hidden md:block">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Carregando…</div>
        ) : tenants.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Nenhum cliente cadastrado ainda.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-brand-50/60 text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b border-brand-100/60">
              <tr>
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">Logo</th>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Segmento</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Criado em</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id} data-testid={`super-tenant-row-${t.slug}`} className="border-t border-zinc-100 hover:bg-brand-50/30">
                  <td className="px-4 py-3">
                    <code data-testid={`super-tenant-code-${t.slug}`} className="rounded bg-brand-50 border border-brand-100 px-2 py-1 text-[11px] font-mono text-brand-700">
                      #{t.client_code || "—"}
                    </code>
                  </td>
                  <td className="px-4 py-3">
                    {t.logo_url ? (
                      <img src={t.logo_url} alt={t.name} className="h-9 w-9 rounded-full object-cover border border-zinc-200" />
                    ) : (
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-100 text-zinc-400">
                        <Building2 size={14} />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-zinc-500">{t.display_name}</div>
                  </td>
                  <td className="px-4 py-3 text-zinc-600" data-testid={`super-tenant-segment-${t.slug}`}>
                    {t.segment || <span className="text-zinc-400 italic">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <code className="rounded bg-brand-50 px-2 py-1 text-[11px] text-brand-700 border border-brand-100">{t.slug}</code>
                  </td>
                  <td className="px-4 py-3">
                    {t.active ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 border border-brand-100 px-2 py-1 text-[11px] font-semibold text-brand-700">Ativo</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-100 px-2 py-1 text-[11px] font-semibold text-red-600">Inativo</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-500">
                    {t.created_at ? new Date(t.created_at).toLocaleDateString("pt-BR") : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <a
                        href={`https://${ROOT_DOMAIN}/${t.slug}`}
                        target="_blank" rel="noreferrer"
                        data-testid={`super-tenant-open-${t.slug}`}
                        className="rounded-full border border-zinc-200 bg-white p-2 text-zinc-500 hover:border-brand-300 hover:text-brand-600"
                        title="Abrir"
                      >
                        <ExternalLink size={13} />
                      </a>
                      <button
                        data-testid={`super-tenant-edit-${t.slug}`}
                        onClick={() => nav(`/super/clientes/${t.id}`)}
                        className="rounded-full border border-zinc-200 bg-white p-2 text-zinc-500 hover:border-brand-300 hover:text-brand-600"
                        title="Editar"
                      >
                        <Pencil size={13} />
                      </button>
                      {t.active && (
                        <button
                          data-testid={`super-tenant-support-${t.slug}`}
                          onClick={() => openSupport(t)}
                          className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100"
                          title="Acessar como suporte"
                        >
                          <LifeBuoy size={12} /> Suporte
                        </button>
                      )}
                      <button
                        data-testid={`super-tenant-toggle-${t.slug}`}
                        onClick={() => toggle(t)}
                        className={`rounded-full border border-zinc-200 bg-white p-2 hover:border-brand-300 ${t.active ? "text-red-500" : "text-brand-600"}`}
                        title={t.active ? "Desativar" : "Ativar"}
                      >
                        {t.active ? <PowerOff size={13} /> : <Power size={13} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Mobile — cards por estabelecimento */}
      <div className="md:hidden space-y-3" data-testid="super-tenants-mobile">
        {loading ? (
          <div className="rounded-2xl border border-zinc-200 bg-white p-6 text-center text-zinc-500 text-sm">Carregando…</div>
        ) : tenants.length === 0 ? (
          <div className="rounded-2xl border border-zinc-200 bg-white p-6 text-center text-zinc-500 text-sm">Nenhum cliente cadastrado ainda.</div>
        ) : (
          tenants.map((t) => (
            <div
              key={t.id}
              data-testid={`super-tenant-card-${t.slug}`}
              className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start gap-3">
                {t.logo_url ? (
                  <img
                    src={t.logo_url}
                    alt={t.name}
                    className="h-12 w-12 rounded-full object-cover border border-zinc-200 flex-shrink-0"
                    onError={(e) => {
                      if (e.currentTarget.dataset.fbk !== "1") {
                        e.currentTarget.dataset.fbk = "1";
                        e.currentTarget.src = "/logo.png";
                      }
                    }}
                  />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100 text-zinc-400 flex-shrink-0">
                    <Building2 size={18} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="rounded bg-brand-50 border border-brand-100 px-1.5 py-0.5 text-[10px] font-mono text-brand-700">
                      #{t.client_code || "—"}
                    </code>
                    {t.active ? (
                      <span className="rounded-full bg-brand-50 border border-brand-100 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700">Ativo</span>
                    ) : (
                      <span className="rounded-full bg-red-50 border border-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-600">Inativo</span>
                    )}
                  </div>
                  <div className="mt-1 font-semibold truncate">{t.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{t.display_name}</div>
                  <div className="mt-1 text-xs text-zinc-500">
                    {t.segment || "—"} · <code className="text-brand-700">{t.slug}</code>
                  </div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  onClick={() => nav(`/super/clientes/${t.id}`)}
                  className="inline-flex items-center justify-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 hover:border-brand-300 hover:text-brand-600"
                >
                  <Pencil size={12} /> Editar
                </button>
                {t.active ? (
                  <button
                    onClick={() => openSupport(t)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100"
                  >
                    <LifeBuoy size={12} /> Suporte
                  </button>
                ) : (
                  <button
                    onClick={() => toggle(t)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-brand-600 hover:border-brand-300"
                  >
                    <Power size={12} /> Ativar
                  </button>
                )}
                <a
                  href={`https://${ROOT_DOMAIN}/${t.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 hover:border-brand-300 hover:text-brand-600"
                >
                  <ExternalLink size={12} /> Abrir
                </a>
                {t.active && (
                  <button
                    onClick={() => toggle(t)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-red-500 hover:border-red-300"
                  >
                    <PowerOff size={12} /> Desativar
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
